// CandidatoCard — exibe 1 candidato (técnico) na lista de recomendação.
// Card #1 (melhor) renderiza expandido; #2/#3 podem ser colapsados.

import { useState } from 'react';
import { COR_STATUS_VISUAL } from '../lib/statusVisual.js';

const COR_POR_SETOR = {
  suporte:        '#1e6fc8',
  ativacao:       '#16a34a',
  recolhimento:   '#ea580c',
  infraestrutura: '#9333ea',
  operacional:    '#6b7280',
};

function formatHoraIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function corPct(pct) {
  if (pct == null) return '#6b7280';
  if (pct >= 70) return '#059669';
  if (pct >= 40) return '#d97706';
  return '#dc2626';
}

export default function CandidatoCard({ candidato, posicao, melhorEscolhaIA, expandido: expandidoInicial }) {
  const [expandido, setExpandido] = useState(expandidoInicial ?? (posicao === 0));
  const c = candidato;
  const m = c.metricas || {};
  const setorCor = COR_POR_SETOR[m.setor_primario] || '#94a3b8';
  const ehMelhorIA = melhorEscolhaIA != null && Number(c.tecnico_id) === Number(melhorEscolhaIA);

  // Iniciais pro avatar
  const iniciais = (c.tecnico_apelido || c.tecnico_nome || '?').split(/\s+/).slice(0, 2)
    .map(s => s[0]).join('').toUpperCase();

  const slot = c.slot_sugerido;
  const corScore = c.score >= 75 ? '#059669' : c.score >= 50 ? '#d97706' : '#dc2626';

  return (
    <div className={"candidato-card" + (ehMelhorIA ? ' melhor-ia' : '')}>
      {ehMelhorIA && (
        <div className="candidato-badge-ia">🏆 Escolha da IA</div>
      )}

      {/* Header */}
      <div className="candidato-header" onClick={() => setExpandido(!expandido)}>
        <div className="candidato-avatar" style={{ background: setorCor }}>
          {iniciais}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15 }}>{c.tecnico_apelido || c.tecnico_nome}</strong>
            {m.setor_primario && (
              <span style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 99,
                background: setorCor + '20', color: setorCor, fontWeight: 600,
              }}>
                {m.setor_primario}
              </span>
            )}
            {c.tipo_contrato === 'PJ' && (
              <span title="PJ — horário flexível, atende por demanda"
                style={{
                  fontSize: 10, padding: '2px 7px', borderRadius: 99,
                  background: '#dbeafe', color: '#1e40af', fontWeight: 700,
                  border: '1px solid #93c5fd',
                }}>
                PJ
              </span>
            )}
            <span style={{ fontSize: 11, color: '#9ca3af' }}>#{posicao + 1}</span>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {slot ? `🕐 Slot sugerido: ${formatHoraIso(slot.inicio)}-${formatHoraIso(slot.fim)}` : '⚠ sem gap viável'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: corScore, lineHeight: 1 }}>
            {c.score}
          </div>
          <div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            score
          </div>
        </div>
        <div style={{ fontSize: 18, color: '#9ca3af', transform: expandido ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</div>
      </div>

      {/* Expandido — métricas, razões, agenda do dia */}
      {expandido && (
        <div className="candidato-body">
          {/* Métricas em grid */}
          <div className="candidato-metricas">
            <div>
              <div className="cm-l">{m.origem_distancia_tipo === 'gps' ? `Onde está agora (GPS ${m.gps_idade_min}min)` : 'Distância de casa'}</div>
              <div className="cm-v">{m.distancia_residencia_km != null ? `${m.distancia_residencia_km}km` : '—'}</div>
            </div>
            <div>
              <div className="cm-l">OS mais próxima</div>
              <div className="cm-v">{m.distancia_intra_rota_km != null ? `${m.distancia_intra_rota_km}km` : '—'}</div>
            </div>
            <div>
              <div className="cm-l">Pontualidade</div>
              <div className="cm-v" style={{ color: corPct(m.pontualidade_pct) }}>
                {m.pontualidade_pct != null ? `${m.pontualidade_pct}%` : '—'}
                <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>
                  {m.pontualidade_amostra ? ` (n=${m.pontualidade_amostra})` : ''}
                </span>
              </div>
            </div>
            <div>
              <div className="cm-l">Carga do dia</div>
              <div className="cm-v">{m.carga_dia_pct}%</div>
            </div>
            <div>
              <div className="cm-l">Velocidade</div>
              <div className="cm-v">{m.coeficiente_velocidade?.toFixed(2)}x</div>
            </div>
            <div>
              <div className="cm-l">Skill nível</div>
              <div className="cm-v">{m.skill_nivel}/5</div>
            </div>
          </div>

          {/* Razões */}
          {c.razoes?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {c.razoes.map((r, i) => (
                <div key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 2 }}>
                  <span style={{ color: '#16a34a', marginRight: 4 }}>✓</span> {r}
                </div>
              ))}
            </div>
          )}

          {/* Alertas */}
          {c.alertas?.length > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', borderRadius: 6, border: '1px solid #fcd34d' }}>
              {c.alertas.map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: '#78350f' }}>
                  ⚠ {a}
                </div>
              ))}
            </div>
          )}

          {/* TRÂNSITO REAL (Google Distance Matrix) — só pro top 1 */}
          {c.transito_real && !c.transito_real.erro && c.transito_real.pernas && (
            <div style={{ marginTop: 12, padding: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
              <div style={{ fontSize: 10, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, marginBottom: 6 }}>
                🚦 Trânsito real (Google Maps) · {c.transito_real.distancia_total_km}km · {c.transito_real.duracao_total_min}min totais
              </div>
              {c.transito_real.pernas.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', color: p.para === 'NOVO' ? '#059669' : '#1e293b', fontWeight: p.para === 'NOVO' ? 700 : 400 }}>
                  <span>{p.de} → {p.para}</span>
                  <span style={{ fontFamily: 'monospace' }}>
                    {p.erro ? `⚠ ${p.erro}` : `${p.distancia_km?.toFixed(1)}km · ${p.duracao_transito_min || p.duracao_min}min`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {c.transito_real?.erro && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
              ⓘ Trânsito real indisponível ({c.transito_real.erro.slice(0, 80)}) — usando estimativa por distância
            </div>
          )}

          {/* Agenda do dia (mini timeline) */}
          {c.agenda_dia?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, marginBottom: 6 }}>
                Agenda do dia · {c.agenda_dia.length} OS
              </div>
              <div className="candidato-agenda">
                {c.agenda_dia.map(v => (
                  <div key={v.id} className="ca-row">
                    <div className="ca-hora">{formatHoraIso(v.janela_inicio)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                        {v.cliente_nome || '?'}
                      </div>
                      <div style={{ fontSize: 10, color: '#6b7280' }}>
                        {v.tipo_os || '?'}{v.cliente_bairro ? ` · ${v.cliente_bairro}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                {/* Slot sugerido (novo cliente) — destacado */}
                {slot && (
                  <div className="ca-row" style={{ background: '#dcfce7', border: '1px dashed #22c55e' }}>
                    <div className="ca-hora" style={{ color: '#059669', fontWeight: 700 }}>{formatHoraIso(slot.inicio)}</div>
                    <div style={{ flex: 1, fontSize: 12, color: '#14532d', fontWeight: 600 }}>
                      ⭐ NOVO CLIENTE (este agendamento)
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
